// Patients and family accounts (modules-and-api.md §5.6, §5.2 handover; PAT-01..PAT-97; BR-01..BR-23; decisions D11, D42,
// D43, D58). The Zod schemas the Worker validates with and the PWA renders from, the pure helpers both share (account
// prefixes and codes, BR-16 folding, BR-07 age), and the SPI declaration of the patient read model. No clinical field
// beyond the PAT-10 free text enters these read models (Sprint 2 build note).
//
// Vocabulary is the API's where the design spelled a value differently (`SaId`, `PatientPhoto`, `TurnedEighteen`,
// `StayWithOriginal` / `TransferToNew`, `Lens` / `ContactLens`); the tenant schema uses the same spellings.
import { z } from "zod";
import { CONSENT_CHANNELS, ConsentInputShape, consentInputIssues, GUARDIAN_RELATIONSHIPS, InlineUploadSchema, UploadRefSchema, UserRefSchema } from "./governance";
import { AccountCodeSchema, AuditStampSchema, CentsSchema, EmailSchema, InstantSchema, LocalDateSchema, NonNegativeCentsSchema, UlidSchema } from "./primitives";
import { fullYearsBetween, parseSaId, saIdSchema } from "./sa-id";
import { spi, type SpiClass } from "./spi";
import { PATIENT_STATUSES, PATIENT_STATUSES_REQUIRING_REASON, type PatientStatus } from "./status-machines";

// --- Enumerations (the values the tenant schema CHECKs accept) ---------------------------------------------------------------

export const ID_TYPES = ["SaId", "Passport", "None"] as const;
export type IdType = (typeof ID_TYPES)[number];
export const GENDERS = ["Male", "Female", "Other"] as const;
export type Gender = (typeof GENDERS)[number];
export const RELATIONSHIPS_TO_MAIN = ["Child", "Spouse", "Parent", "Staff", "Other"] as const;
export type RelationshipToMain = (typeof RELATIONSHIPS_TO_MAIN)[number];
/** PAT-76 verbatim. */
export const CONTACT_CHANNELS = ["Email", "WhatsApp", "SMS", "PhoneCall", "None"] as const;
export type ContactChannel = (typeof CONTACT_CHANNELS)[number];
export const PREFERRED_LANGUAGES = ["en", "af", "other"] as const;
/** The reasons a patient row may carry (PAT-49..PAT-54): Prohibited DidNotPay/Rude/Other, Archived Moved/Deceased/Other, Dormant AutoInactive/Other. */
export const PATIENT_STATUS_REASON_CODES = ["DidNotPay", "Rude", "Moved", "Deceased", "AutoInactive", "Other"] as const;
export type PatientStatusReasonCode = (typeof PATIENT_STATUS_REASON_CODES)[number];
export const PATIENT_STATUS_HISTORY_REASON_CODES = ["DidNotPay", "Rude", "Moved", "Deceased", "AutoInactive", "Reactivated", "Unarchived", "Other"] as const;
export const PATIENT_STATUS_SOURCES = ["Manual", "AutoDormancy", "Import", "CascadeFromMainAccount"] as const;
export const PATIENT_CONSENT_STATUSES = ["None", "Current", "Outdated", "Withdrawn"] as const;
export type PatientConsentStatus = (typeof PATIENT_CONSENT_STATUSES)[number];
export const PATIENT_CREATED_SOURCES = ["Staff", "InlineFromDocument", "KioskRegistration", "OnlineRegistration", "Import", "Merge"] as const;
export const LEGACY_SOURCES = ["LegacyTerminal", "ZohoCRM", "Spreadsheet", "Other"] as const;
export const PRINCIPAL_MEMBER_IS = ["Self", "MainAccount", "LinkedPatient", "OtherPerson"] as const;
export type PrincipalMemberIs = (typeof PRINCIPAL_MEMBER_IS)[number];
export const RELATIONSHIPS_TO_PRINCIPAL_MEMBER = ["Child", "Spouse", "Other"] as const;
/** §5.6 spelling (OQ-P2 resolved in the API's favour): `Lens`, `ContactLens`. */
export const COVERAGE_OVERRIDE_TYPES = ["Consultation", "Lens", "Frame", "ContactLens"] as const;
export type CoverageOverrideType = (typeof COVERAGE_OVERRIDE_TYPES)[number];
export const BENEFIT_YEAR_BASES = ["CalendarYear", "SchemeAnniversary", "MemberJoinDate"] as const;
export const PATIENT_DOCUMENT_TYPES = ["IdDocument", "DriversLicence", "MedicalAidCardFront", "MedicalAidCardBack", "PatientPhoto", "PatientCardDrawing", "PopiaConsent", "Other"] as const;
export type PatientDocumentType = (typeof PATIENT_DOCUMENT_TYPES)[number];
/** Types a client may upload; PopiaConsent is generated by the consent service, PatientCardDrawing arrives with the clinical module (Sprint 3). */
export const UPLOADABLE_DOCUMENT_TYPES: readonly PatientDocumentType[] = ["IdDocument", "DriversLicence", "MedicalAidCardFront", "MedicalAidCardBack", "PatientPhoto", "Other"];
/** Each type in the words the screens use — and the start of a document's file name (BR-PATIENTS-030). */
export const PATIENT_DOCUMENT_TYPE_LABELS: Readonly<Record<PatientDocumentType, string>> = {
  IdDocument: "ID document", DriversLicence: "Driver's licence", MedicalAidCardFront: "Medical aid card (front)", MedicalAidCardBack: "Medical aid card (back)",
  PatientPhoto: "Patient photo", PatientCardDrawing: "Patient card drawing", PopiaConsent: "POPIA consent", Other: "Other document",
};
export const CAPTURE_METHODS = ["Camera", "Upload", "Drawn", "Generated", "Import"] as const;
/** What the document-access log records about an open (PAT-28): shown, saved, printed, or sent to the patient. */
export const DOCUMENT_ACCESS_ACTIONS = ["View", "Download", "Print", "Send"] as const;
export type DocumentAccessAction = (typeof DOCUMENT_ACCESS_ACTIONS)[number];
export const DOCUMENT_LINKED_RECORD_TYPES = ["prescription", "appointment", "examination", "sales_order", "consent_record", "registration_submission"] as const;
export const DETACH_MODES = ["NewAccount", "ExistingAccount"] as const;
export const DETACH_REASONS = ["TurnedEighteen", "Divorce", "OwnMedicalAid", "Other"] as const;
export const OPEN_ITEMS_DECISIONS = ["StayWithOriginal", "TransferToNew"] as const;
export const ACCOUNT_NUMBER_CHANGE_REASONS = ["Created", "DetachNew", "DetachToExisting", "Merge", "ManualCorrection", "Import"] as const;
export const REGISTRATION_SOURCES = ["InStore", "Online"] as const;
export const REGISTRATION_KINDS = ["NewPatient", "UpdateExisting"] as const;
export const REGISTRATION_STATUSES = ["Pending", "Approved", "Rejected", "Expired"] as const;
/** §5.2: what a handover opens — the three-step registration (new, or an existing patient's update), consent alone, or the medical history alone. */
export const HANDOVER_FORM_TYPES = ["Registration", "Update", "Consent", "MedicalHistory"] as const;
export type HandoverFormType = (typeof HANDOVER_FORM_TYPES)[number];
/** BR-10 / BR-PATIENTS-006: `ExactId` is blocking; `SurnameDob` and `SurnameMobile` are probable; `FirstNamesSurname` is possible. */
export const DUPLICATE_MATCH_RULES = ["ExactId", "SurnameDob", "SurnameMobile", "FirstNamesSurname"] as const;
export const SEARCH_MATCH_KINDS = ["accountCode", "historicAccountCode", "surname", "firstName", "idNumber", "phone", "email", "medicalAidNumber", "cardFilingNumber", "dateOfBirth"] as const;
/** PAT-90, PAT-96, PAT-97: the chips on the patient header (BR-PATIENTS-019 adds the main-account cascade and the two identity alerts). */
export const PATIENT_ALERT_KINDS = ["Prohibited", "Dormant", "Deceased", "MainAccountProhibited", "MainAccountArchived", "ConsentMissing", "ConsentOutdated", "ConsentWithdrawn", "MissingIdScan", "MissingMedicalAidCard", "CardPossiblyOutdated", "DependantTurnedEighteen", "OwnMedicalAidDependant", "LegacyBalancePossible", "Incomplete", "DuplicateCardFilingNumber", "IdUnverified", "IdDobGenderMismatch"] as const;
export type PatientAlertKind = (typeof PATIENT_ALERT_KINDS)[number];
/** PAT-97: the fields the CRM import does not reliably hold; missing ones are prompted for at the first visit. */
export const COMPLETENESS_ITEMS = ["accountCode", "mainAccountRelationship", "medicalAid", "dependentCode", "cardFilingNumber", "idNumber", "dateOfBirth", "consent", "clinicalHistory"] as const;
export type CompletenessItem = (typeof COMPLETENESS_ITEMS)[number];
export const PATIENT_SORTS = ["surname", "accountCode", "createdAt", "lastVisitDate"] as const;

/** PAT-02 / Q-01: the five SOW titles seeded per tenant at provisioning; tenants add their own. */
export const SEEDED_PATIENT_TITLES: readonly { code: string; label: string; sortOrder: number }[] = [
  { code: "Mr", label: "Mr", sortOrder: 10 }, { code: "Mrs", label: "Mrs", sortOrder: 20 }, { code: "Miss", label: "Miss", sortOrder: 30 },
  { code: "Dr", label: "Dr", sortOrder: 40 }, { code: "Prof", label: "Prof", sortOrder: 50 },
];

// --- Pure helpers shared by the Worker, the PWA and the kiosk (BR-01, BR-02, BR-03, BR-07, BR-16) --------------------------

/** BR-16 folding: lower-cased, diacritics stripped ("Naudé" → "naude"), apostrophes and punctuation removed ("O'Neil" → "oneil"), hyphens become spaces, whitespace collapsed. */
export function foldName(s: string): string {
  return s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[-‐-―]/g, " ").replace(/[^a-z0-9\s]/g, "").replace(/\s+/g, " ").trim();
}
/** The search tokens of a name: each word of the folded form ("Le Roux" → ["le", "roux"]). */
export function nameTokens(s: string): string[] {
  return foldName(s).split(" ").filter((t) => t.length > 0);
}
/** BR-01: the first three letters of the folded surname, upper-cased; shorter surnames are right-padded with X ("Ng" → "NGX"). Fixed at creation. */
export function accountPrefixFor(surname: string): string {
  const letters = foldName(surname).replace(/[^a-z]/g, "").toUpperCase();
  return (letters + "XXX").slice(0, 3);
}
/** BR-02/BR-03: `NAU001/0` — the sequence is at least three digits, the dependant suffix unpadded. */
export function formatAccountCode(prefix: string, sequence: number, dependentSuffix: number): string {
  return `${prefix}${String(sequence).padStart(3, "0")}/${dependentSuffix}`;
}
export interface AccountCodeParts { prefix: string; sequence: number; dependentSuffix: number }
export function parseAccountCode(code: string): AccountCodeParts | null {
  const m = /^([A-Z]{3})([0-9]{3,})\/([0-9]+)$/.exec(code);
  if (!m) return null;
  return { prefix: m[1]!, sequence: Number(m[2]), dependentSuffix: Number(m[3]) };
}
export const ACCOUNT_PREFIX_RE = /^[A-Z]{3}$/;

export interface NormalisedMobile {
  /** As keyed, trimmed. */
  display: string;
  /** '+27825551234' — the value adapters send. */
  e164: string;
  /** Digits only, leading 0 stripped, +27 folded ('825551234'): the BR-16 search and duplicate key. */
  digits: string;
}
/** BR-16: 0 / +27 / 0027 forms of a South African number are one number; a foreign `+` number keeps its country code. Null when not a phone number. */
export function normaliseMobile(input: string): NormalisedMobile | null {
  const display = input.trim();
  const raw = display.replace(/[^\d+]/g, "");
  if (raw.length === 0) return null;
  let national: string | null = null;
  let e164: string;
  if (raw.startsWith("+27")) national = raw.slice(3);
  else if (raw.startsWith("0027")) national = raw.slice(4);
  else if (raw.startsWith("27") && raw.length === 11) national = raw.slice(2);
  else if (raw.startsWith("0")) national = raw.slice(1);
  else if (/^[1-9]\d{8}$/.test(raw)) national = raw;
  if (national !== null) {
    if (!/^[1-9]\d{8}$/.test(national)) return null;
    e164 = `+27${national}`;
    return { display, e164, digits: national };
  }
  if (raw.startsWith("+") && /^\+[1-9]\d{6,14}$/.test(raw)) return { display, e164: raw, digits: raw.slice(1) };
  return null;
}
export const normaliseEmail = (s: string): string => s.trim().toLowerCase();
/** PAT-04: derived from the first names ("Thandiwe Nomvula" → "TN"); manually overridable. */
export const initialsOf = (firstNames: string): string => nameTokens(firstNames).map((t) => t[0]!.toUpperCase()).join("");
/** PAT-47 / BR-20: upper-cased, no spaces — the search key. */
export const normaliseCardFilingNumber = (s: string): string => s.replace(/\s+/g, "").toUpperCase();
/** BR-07: full years at `at`; never stored. */
export const ageAt = (dateOfBirth: string, at: string | Date): number => fullYearsBetween(dateOfBirth, at);
/** BR-07 / PAT-44: a dependant who is 18 or older at `at`. */
export const turnedEighteen = (dateOfBirth: string, at: string | Date): boolean => ageAt(dateOfBirth, at) >= 18;

// --- Shared fragments --------------------------------------------------------------------------------------------------------

const text = (max: number) => z.string().trim().max(max);
const optionalText = (max: number) => z.string().trim().max(max).nullable().optional();
const Country = z.string().trim().length(2).toUpperCase();
const OpaqueId = z.string().trim().min(1).max(64);
const Limit = z.coerce.number().int().min(1).max(200).default(50);
const Flag = z.enum(["true", "false"]);

/** South Africa's nine provinces, as a South African address's province is chosen from them (PAT-09; owner, 8 Oct 2026). */
export const SA_PROVINCES = ["Eastern Cape", "Free State", "Gauteng", "KwaZulu-Natal", "Limpopo", "Mpumalanga", "Northern Cape", "North West", "Western Cape"] as const;

export const AddressSchema = z.object({
  line1: z.string().nullable(), line2: z.string().nullable(), suburb: z.string().nullable(), city: z.string().nullable(),
  province: z.string().nullable(), postalCode: z.string().nullable(), country: z.string(),
});
export type Address = z.infer<typeof AddressSchema>;
export const AddressInputSchema = z.strictObject({
  line1: optionalText(200), line2: optionalText(200), suburb: optionalText(120), city: optionalText(120),
  province: optionalText(120), postalCode: optionalText(20), country: Country.optional(),
});
export type AddressInput = z.infer<typeof AddressInputSchema>;

export const PatientContactViewSchema = z.object({
  mobile: z.string().nullable(),
  email: z.string().nullable(),
  altPhone: z.string().nullable(),
  preferredChannel: z.enum(CONTACT_CHANNELS),
  preferredLanguage: z.enum(PREFERRED_LANGUAGES),
  noElectronicContact: z.boolean(),
  /** PAT-37: contact and address are read from the main account at use time. */
  useMainAccountContact: z.boolean(),
  whatsappOptIn: z.boolean(),
  /** The details in force: the patient's own, or the main account's when `useMainAccountContact` (read at use time, never copied). Always sent; optional for readers of the foundation's shape. */
  effective: z.object({ mobile: z.string().nullable(), email: z.string().nullable(), altPhone: z.string().nullable(), fromPatientId: UlidSchema.nullable() }).optional(),
});
export type PatientContactView = z.infer<typeof PatientContactViewSchema>;
export const PatientContactInputSchema = z.strictObject({
  mobile: optionalText(40),
  email: EmailSchema.nullable().optional(),
  altPhone: optionalText(40),
  preferredChannel: z.enum(CONTACT_CHANNELS).optional(),
  preferredLanguage: z.enum(PREFERRED_LANGUAGES).optional(),
  noElectronicContact: z.boolean().optional(),
  useMainAccountContact: z.boolean().optional(),
  whatsappOptIn: z.boolean().optional(),
});
export type PatientContactInput = z.infer<typeof PatientContactInputSchema>;

/** A patient as other records name it (family panel, appointments, search, inbox suggestions). */
export const PatientSummarySchema = z.object({
  id: UlidSchema,
  accountCode: z.string(),
  displayName: z.string(),
  firstNames: z.string(),
  surname: z.string(),
  dateOfBirth: LocalDateSchema,
  age: z.number().int(),
  gender: z.enum(GENDERS),
  status: z.enum(PATIENT_STATUSES),
  homeBranchId: UlidSchema,
  mainAccountId: UlidSchema.nullable(),
});
export type PatientSummary = z.infer<typeof PatientSummarySchema>;

export const PatientAlertSchema = z.object({
  kind: z.enum(PATIENT_ALERT_KINDS),
  severity: z.enum(["info", "warning", "blocking"]),
  message: z.string(),
  /** The family member the alert is about, when not this patient. */
  relatedPatientId: UlidSchema.nullable(),
  /** Structured facts behind the chip: the status reason, the related account code, the missing items, the consent versions. */
  detail: z.record(z.string(), z.unknown()).optional(),
});
export type PatientAlert = z.infer<typeof PatientAlertSchema>;

// --- Medical aid and the PAT-94 principal member ------------------------------------------------------------------------------

export const PrincipalMemberBlockSchema = z.object({
  title: z.string().nullable(), initials: z.string().nullable(), firstNames: z.string(), surname: z.string(),
  idType: z.enum(ID_TYPES).nullable(), idNumber: z.string().nullable(), countryOfIssue: z.string().nullable(),
  dateOfBirth: LocalDateSchema.nullable(), gender: z.enum(GENDERS).nullable(),
  /** BR-23: defaults to the patient's medical-aid number when blank. */
  membershipNumber: z.string().nullable(),
  mobile: z.string().nullable(), addressLine1: z.string().nullable(), addressLine2: z.string().nullable(), city: z.string().nullable(), postalCode: z.string().nullable(),
  relationshipToPrincipalMember: z.enum(RELATIONSHIPS_TO_PRINCIPAL_MEMBER).nullable(),
});
export type PrincipalMemberBlock = z.infer<typeof PrincipalMemberBlockSchema>;
export const PrincipalMemberBlockInputSchema = z.strictObject({
  title: optionalText(20), initials: optionalText(10), firstNames: text(120).min(1), surname: text(120).min(1),
  idType: z.enum(ID_TYPES).optional(), idNumber: optionalText(32), countryOfIssue: Country.nullable().optional(),
  dateOfBirth: LocalDateSchema.nullable().optional(), gender: z.enum(GENDERS).nullable().optional(),
  membershipNumber: optionalText(40),
  mobile: optionalText(40), addressLine1: optionalText(200), addressLine2: optionalText(200), city: optionalText(120), postalCode: optionalText(20),
  relationshipToPrincipalMember: z.enum(RELATIONSHIPS_TO_PRINCIPAL_MEMBER).optional(),
}).superRefine((v, ctx) => {
  if (v.idType === "SaId" && v.idNumber) {
    const r = saIdSchema.safeParse(v.idNumber);
    if (!r.success) ctx.addIssue({ code: "custom", path: ["idNumber"], message: "not a valid South African ID number", params: { code: "luhn" } });
  }
  if (v.idType === "Passport" && v.idNumber && !v.countryOfIssue) ctx.addIssue({ code: "custom", path: ["countryOfIssue"], message: "a passport needs a country of issue", params: { code: "required" } });
});
export type PrincipalMemberBlockInput = z.infer<typeof PrincipalMemberBlockInputSchema>;

/** §5.6 PatientMedicalAidView. Scheme, plan and option ids are the platform reference ids (D30). */
export const PatientMedicalAidViewSchema = z.object({
  /** Added in the Sprint 2 build; optional so a reader written against the foundation's shape still parses — the Worker always sends it. */
  id: UlidSchema.optional(),
  sameAsMainAccount: z.boolean(),
  /** BR-17: whose membership row the scheme, plan, option and number were read from (always sent; optional as `id`). */
  source: z.enum(["Own", "MainAccount"]).optional(),
  schemeId: z.string().nullable(), planId: z.string().nullable(), optionId: z.string().nullable(),
  schemeName: z.string().nullable(), planName: z.string().nullable(), optionName: z.string().nullable(),
  membershipNumber: z.string().nullable(),
  dependentCode: z.string().nullable(),
  principalMemberIs: z.enum(PRINCIPAL_MEMBER_IS),
  linkedPatientId: UlidSchema.nullable(),
  principalMember: PrincipalMemberBlockSchema.nullable(),
  notes: z.string().nullable(),
  cardDocumentId: UlidSchema.nullable(),
  /** PAT-86: the scheme or number changed after the current card scan. */
  cardOutdatedAt: InstantSchema.nullable(),
  effectiveFrom: LocalDateSchema,
  effectiveTo: LocalDateSchema.nullable().optional(),
  isCurrent: z.boolean().optional(),
  /** The stored plan or option has since been discontinued in the reference data (still displayed, refused for a new save). */
  discontinued: z.boolean().optional(),
});
export type PatientMedicalAidView = z.infer<typeof PatientMedicalAidViewSchema>;
/** GET / PUT /patients/{id}/medical-aid: the current membership (or none) and the history, newest first. */
export const PatientMedicalAidReadSchema = z.object({
  current: PatientMedicalAidViewSchema.nullable(),
  history: z.array(PatientMedicalAidViewSchema),
  warnings: z.array(z.object({ path: z.string(), code: z.string(), message: z.string() })),
});
export type PatientMedicalAidRead = z.infer<typeof PatientMedicalAidReadSchema>;
/** GET /patients/{id}/medical-aid/principal-member: the member as derived from `principalMemberIs` (BR-23). */
export const PrincipalMemberReadSchema = z.object({
  source: z.enum(PRINCIPAL_MEMBER_IS),
  member: PrincipalMemberBlockSchema.nullable(),
  /** BR-23: the full set is demanded only before a line with a medical-aid portion is invoiced (Sprint 5). */
  complete: z.boolean(),
  missing: z.array(z.string()),
});
export type PrincipalMemberRead = z.infer<typeof PrincipalMemberReadSchema>;

export const PatientMedicalAidInputSchema = z.strictObject({
  /** BR-17: a dependant reads the main account's membership at use time; only a dependant may set it. */
  sameAsMainAccount: z.boolean().optional(),
  schemeId: OpaqueId.nullable().optional(), planId: OpaqueId.nullable().optional(), optionId: OpaqueId.nullable().optional(),
  membershipNumber: optionalText(40),
  dependentCode: optionalText(10),
  principalMemberIs: z.enum(PRINCIPAL_MEMBER_IS).optional(),
  linkedPatientId: UlidSchema.nullable().optional(),
  principalMember: PrincipalMemberBlockInputSchema.nullable().optional(),
  notes: optionalText(4000),
  effectiveFrom: LocalDateSchema.optional(),
}).superRefine((v, ctx) => {
  const memberIs = v.principalMemberIs ?? (v.sameAsMainAccount ? "MainAccount" : "Self");
  if (memberIs === "LinkedPatient" && !v.linkedPatientId) ctx.addIssue({ code: "custom", path: ["linkedPatientId"], message: "a linked principal member names the patient", params: { code: "required" } });
  if (memberIs === "OtherPerson" && !v.principalMember) ctx.addIssue({ code: "custom", path: ["principalMember"], message: "the principal member's details are required", params: { code: "required" } });
  if (v.sameAsMainAccount && memberIs !== "Self" && memberIs !== "MainAccount") ctx.addIssue({ code: "custom", path: ["principalMemberIs"], message: "a membership read from the main account has the main account as its member (BR-23)", params: { code: "contradiction" } });
  if (!v.sameAsMainAccount && !v.schemeId && !v.membershipNumber) ctx.addIssue({ code: "custom", path: ["schemeId"], message: "a scheme or a membership number is required; send null for no medical aid", params: { code: "required" } });
});
export type PatientMedicalAidInput = z.infer<typeof PatientMedicalAidInputSchema>;
/**
 * PUT /patients/{id}/medical-aid: the new membership, or null to end the current one (a patient with no medical aid has no
 * current row); `cardDocumentId` links the patient's current card scan to it (UC-PATIENTS-015 A4, E6).
 */
export const PatientMedicalAidPutSchema = z.strictObject({ medicalAid: PatientMedicalAidInputSchema.nullable(), cardDocumentId: UlidSchema.nullable().optional() });
export type PatientMedicalAidPut = z.infer<typeof PatientMedicalAidPutSchema>;
/** PUT /patients/{id}/medical-aid/principal-member: the PAT-94 block, or null to revert to Self. */
export const PrincipalMemberPutSchema = z.strictObject({ principalMemberIs: z.enum(PRINCIPAL_MEMBER_IS), linkedPatientId: UlidSchema.nullable().optional(), principalMember: PrincipalMemberBlockInputSchema.nullable().optional() }).superRefine((v, ctx) => {
  if (v.principalMemberIs === "LinkedPatient" && !v.linkedPatientId) ctx.addIssue({ code: "custom", path: ["linkedPatientId"], message: "a linked principal member names the patient", params: { code: "required" } });
  if (v.principalMemberIs === "OtherPerson" && !v.principalMember) ctx.addIssue({ code: "custom", path: ["principalMember"], message: "the principal member's details are required", params: { code: "required" } });
});

// --- The patient read model (§5.6 PatientView) --------------------------------------------------------------------------------

export const CompletenessSchema = z.object({ isComplete: z.boolean(), missing: z.array(z.enum(COMPLETENESS_ITEMS)) });

export const PatientViewSchema = z.object({
  id: UlidSchema,
  accountCode: z.string(),
  mainAccountId: UlidSchema.nullable(),
  dependentSuffix: z.number().int(),
  relationshipToMain: z.enum(RELATIONSHIPS_TO_MAIN).nullable(),
  title: z.string().nullable(),
  firstNames: z.string(),
  surname: z.string(),
  initials: z.string(),
  knownAs: z.string().nullable(),
  dateOfBirth: LocalDateSchema,
  /** Derived (BR-07). */
  age: z.number().int(),
  gender: z.enum(GENDERS),
  idType: z.enum(ID_TYPES),
  /** SPI: identity. */
  idNumber: z.string().nullable(),
  idCountryOfIssue: z.string().nullable(),
  /** An Admin saved a number that failed BR-06 (dirty take-on). */
  idUnverified: z.boolean(),
  contact: PatientContactViewSchema,
  address: AddressSchema,
  postalAddress: AddressSchema.nullable(),
  homeBranchId: UlidSchema,
  cardFilingNumber: z.string().nullable(),
  status: z.enum(PATIENT_STATUSES),
  statusReasonCode: z.enum(PATIENT_STATUS_REASON_CODES).nullable(),
  statusReason: z.string().nullable(),
  statusChangedAt: InstantSchema.nullable(),
  isDeceased: z.boolean(),
  /** SPI: identity. */
  medicalAid: PatientMedicalAidViewSchema.nullable(),
  // medicalHistory (PAT-10, `health`) is write-only this sprint: accepted by CreatePatientInput, PatientPatch and the kiosk
  // submission, stored, never read back — no clinical field enters a Sprint 2 read model (docs/use-cases/patients.md).
  /** From governance (BR-15). */
  consentStatus: z.enum(PATIENT_CONSENT_STATUSES),
  /** From billing — a read-only projection (C-3); the cache until the ledger exists. */
  receivablesCents: CentsSchema,
  /** Signed, 15 minutes. */
  photoUrl: z.string().nullable(),
  alerts: z.array(PatientAlertSchema),
  /** PAT-97. */
  completeness: CompletenessSchema,
  /** INT-85 / BR-APPOINTMENTS-017: the last recall-source completion plus the interval (the patient's override wins). */
  recallDueDate: LocalDateSchema.nullable(),
  recallIntervalMonthsOverride: z.number().int().nullable(),
  /** PAT-96 / D10a. */
  legacyBalancePossible: z.boolean(),
  legacyAccountCode: z.string().nullable(),
  createdSource: z.enum(PATIENT_CREATED_SOURCES),
  isStaffAccount: z.boolean(),
  isManualAccountCode: z.boolean(),
  version: z.number().int(),
  audit: AuditStampSchema,
});
export type PatientView = z.infer<typeof PatientViewSchema>;
export const PatientWarningSchema = z.object({ path: z.string(), code: z.string(), message: z.string(), relatedPatientId: UlidSchema.nullable().optional(), detail: z.record(z.string(), z.unknown()).optional() });
export type PatientWarning = z.infer<typeof PatientWarningSchema>;
/** POST /patients and PATCH /patients/{id}: the view plus the non-blocking warnings of the save (BR-06 mismatches, a duplicate card filing number). */
export const PatientWriteResultSchema = PatientViewSchema.extend({ warnings: z.array(PatientWarningSchema) });
export type PatientWriteResult = z.infer<typeof PatientWriteResultSchema>;
/** POST /patients/{id}/status: the view and whether anything changed (a move to the current status writes nothing). */
export const StatusChangeResultSchema = PatientViewSchema.extend({ changed: z.boolean() });
/** §2.2(c): the classified fields of the read model — redacted by role at serialisation, dropped from every diff and export. */
export const PatientViewSpi = spi(PatientViewSchema, { idNumber: "identity", medicalAid: "identity" });
/** The audit-diff classification the patients module registers for the `patient` table (UC-DB-007, TEN-72): column names as stored. */
export const PATIENT_AUDIT_CLASSIFICATION: Readonly<Record<string, SpiClass>> = { id_number: "identity", medical_history: "health" };
export const PATIENT_MEDICAL_AID_AUDIT_CLASSIFICATION: Readonly<Record<string, SpiClass>> = {
  medical_aid_number: "identity", principal_member_id_number: "identity", principal_member_first_names: "identity", principal_member_surname: "identity",
  principal_member_dob: "identity", principal_member_membership_number: "identity", principal_member_mobile_e164: "identity",
};
/** An overall coverage override names the membership its cap applies to (PAT-38): the medical-aid number is `identity` (BR-PATIENTS-016). */
export const PATIENT_MA_OVERALL_OVERRIDE_AUDIT_CLASSIFICATION: Readonly<Record<string, SpiClass>> = { member_no: "identity" };
/** Which document types are Special Personal Information (PAT-28, TEN-66): identity documents and cards are `identity`, a card drawing is `health`. */
export function documentSpiClass(type: PatientDocumentType): SpiClass | null {
  switch (type) {
    case "IdDocument": case "DriversLicence": case "MedicalAidCardFront": case "MedicalAidCardBack": case "PatientPhoto": case "PopiaConsent": return "identity";
    case "PatientCardDrawing": return "health";
    case "Other": return null;
  }
}

// --- Create and patch (§5.6 CreatePatientInput, PatientPatch; BR-06, BR-09, BR-10, D42) -------------------------------------

const DuplicateOverrideSchema = z.strictObject({ ofPatientId: UlidSchema, reason: text(500).min(1) });

const PatientFieldsShape = {
  title: optionalText(20),
  firstNames: text(120).min(1),
  surname: text(120).min(1),
  initials: optionalText(10),
  knownAs: optionalText(80),
  /** Defaults to SaId when an idNumber is given, else None. */
  idType: z.enum(ID_TYPES).optional(),
  idNumber: optionalText(32),
  idCountryOfIssue: Country.nullable().optional(),
  /** Required unless a valid SA ID derives it (BR-06). */
  dateOfBirth: LocalDateSchema.optional(),
  gender: z.enum(GENDERS).optional(),
  /** BR-06: the entered date of birth or gender disagrees with the ID number and staff accept it. */
  acknowledgeIdMismatch: z.boolean().optional(),
  /** BR-06 / UC-PATIENTS-002 A5: an Owner or Admin saves an SA ID number that fails validation (a dirty take-on); nothing is derived from it. */
  idUnverified: z.boolean().optional(),
  contact: PatientContactInputSchema.optional(),
  address: AddressInputSchema.optional(),
  /** Null = same as the physical address. */
  postalAddress: AddressInputSchema.nullable().optional(),
  homeBranchId: UlidSchema.optional(),
  cardFilingNumber: optionalText(20),
  medicalHistory: optionalText(20000),
  medicalAid: PatientMedicalAidInputSchema.nullable().optional(),
  isStaffAccount: z.boolean().optional(),
  linkedUserId: UlidSchema.nullable().optional(),
  /** INT-85: a per-patient recall interval that wins over the type's; null = the type's. */
  recallIntervalMonthsOverride: z.number().int().min(1).max(120).nullable().optional(),
} as const;

export interface PatientDerivations {
  idType: IdType;
  dateOfBirth: string | null;
  gender: Gender | null;
  /** BR-06 non-blocking mismatches between the ID number and what was entered. */
  warnings: { path: string; code: string; message: string }[];
}
/** The effective identity fields of a create/approve body: entered values stand, a valid SA ID fills the gaps (BR-06). */
export function derivePatientIdentity(v: { idType?: IdType | undefined; idNumber?: string | null | undefined; dateOfBirth?: string | undefined; gender?: Gender | undefined }, today: Date | string = new Date()): PatientDerivations {
  const idType: IdType = v.idType ?? (v.idNumber ? "SaId" : "None");
  const parsed = idType === "SaId" && v.idNumber ? parseSaId(v.idNumber, today) : null;
  const warnings: PatientDerivations["warnings"] = [];
  if (parsed && v.dateOfBirth && v.dateOfBirth !== parsed.dateOfBirth) warnings.push({ path: "dateOfBirth", code: "dob_mismatch", message: `the ID number gives ${parsed.dateOfBirth}` });
  if (parsed && v.gender && v.gender !== parsed.gender) warnings.push({ path: "gender", code: "gender_mismatch", message: `the ID number gives ${parsed.gender}` });
  return { idType, dateOfBirth: v.dateOfBirth ?? parsed?.dateOfBirth ?? null, gender: v.gender ?? parsed?.gender ?? null, warnings };
}

function identityIssues(v: { idType?: IdType | undefined; idNumber?: string | null | undefined; idCountryOfIssue?: string | null | undefined; dateOfBirth?: string | undefined; gender?: Gender | undefined; idUnverified?: boolean | undefined }, ctx: z.RefinementCtx, requireDobAndGender: boolean): void {
  const idType = v.idType ?? (v.idNumber ? "SaId" : "None");
  let identityValid = true;
  if (idType === "SaId") {
    if (!v.idNumber) { identityValid = false; ctx.addIssue({ code: "custom", path: ["idNumber"], message: "an SA ID number is required for idType SaId", params: { code: "required" } }); }
    else if (v.idUnverified) {
      // UC-PATIENTS-002 A5: the number is stored as transcribed and nothing is derived from it, so the date of birth must be entered.
      identityValid = false;
      if (requireDobAndGender && !v.dateOfBirth) ctx.addIssue({ code: "custom", path: ["dateOfBirth"], message: "date of birth is required with an unverified ID number", params: { code: "required" } });
      if (requireDobAndGender && !v.gender) ctx.addIssue({ code: "custom", path: ["gender"], message: "gender is required with an unverified ID number", params: { code: "required" } });
    } else {
      const r = saIdSchema.safeParse(v.idNumber);
      if (!r.success) identityValid = false;
      for (const issue of r.success ? [] : r.error.issues) ctx.addIssue({ code: "custom", path: ["idNumber"], message: issue.message, params: { code: (issue as { params?: { code?: string } }).params?.code ?? "invalid" } });
    }
  }
  if (idType === "Passport") {
    if (!v.idNumber) ctx.addIssue({ code: "custom", path: ["idNumber"], message: "a passport number is required for idType Passport", params: { code: "required" } });
    if (!v.idCountryOfIssue) ctx.addIssue({ code: "custom", path: ["idCountryOfIssue"], message: "a passport needs a country of issue", params: { code: "required" } });
  }
  if (idType === "None" && v.idNumber) ctx.addIssue({ code: "custom", path: ["idType"], message: "an ID number needs an ID type", params: { code: "required" } });
  // The date of birth and gender are asked for only once the identity itself is right: an invalid ID number is fixed first.
  if (requireDobAndGender && identityValid) {
    const derived = derivePatientIdentity(v);
    if (!derived.dateOfBirth) ctx.addIssue({ code: "custom", path: ["dateOfBirth"], message: "date of birth is required unless a valid SA ID number gives it", params: { code: "required" } });
    if (!derived.gender) ctx.addIssue({ code: "custom", path: ["gender"], message: "gender is required unless a valid SA ID number gives it", params: { code: "required" } });
  }
}
/** BR-09: contactable, or explicitly not; a dependant reading the main account's details satisfies it (PAT-77). */
function contactIssues(contact: PatientContactInput | undefined, ctx: z.RefinementCtx, path: (string | number)[] = ["contact"], requireOne = true): void {
  const c = contact ?? {};
  if (requireOne && !c.mobile && !c.email && !c.noElectronicContact && !c.useMainAccountContact) ctx.addIssue({ code: "custom", path: [...path, "mobile"], message: "a mobile number, an email address, or the no-electronic-contact acknowledgement is required (BR-09)", params: { code: "one_of_required" } });
  if (c.mobile && normaliseMobile(c.mobile) === null) ctx.addIssue({ code: "custom", path: [...path, "mobile"], message: "not a phone number", params: { code: "invalid" } });
  if (c.altPhone && normaliseMobile(c.altPhone) === null) ctx.addIssue({ code: "custom", path: [...path, "altPhone"], message: "not a phone number", params: { code: "invalid" } });
}

export const CreatePatientInputSchema = z.strictObject({
  ...PatientFieldsShape,
  /** A dependant of this main account (PAT-33); absent = a new main account. */
  mainAccountId: UlidSchema.optional(),
  relationshipToMain: z.enum(RELATIONSHIPS_TO_MAIN).optional(),
  /** D42: continue past an exact-ID match with a typed, logged reason. */
  duplicateOverride: DuplicateOverrideSchema.optional(),
  /** BR-10: continue past a probable match (same surname and date of birth or mobile) once it has been seen; audited. */
  duplicateAcknowledged: z.boolean().optional(),
  /** PAT-32: an Admin assigns the code for take-on continuity; must be unique. */
  manualAccountCode: AccountCodeSchema.optional(),
}).superRefine((v, ctx) => {
  identityIssues(v, ctx, true);
  // A dependant may read the main account's details (PAT-37): a minor with none of its own is defaulted to that by the
  // service (UC-PATIENTS-007 step 2), an adult is refused there; a main account must be contactable here (BR-09).
  contactIssues(v.contact, ctx, ["contact"], !v.mainAccountId);
  if (v.mainAccountId && !v.relationshipToMain) ctx.addIssue({ code: "custom", path: ["relationshipToMain"], message: "a dependant's relationship to the main account is required (PAT-34)", params: { code: "required" } });
  if (!v.mainAccountId && v.relationshipToMain) ctx.addIssue({ code: "custom", path: ["relationshipToMain"], message: "a main account has no relationship to itself", params: { code: "not_allowed" } });
  if (!v.mainAccountId && v.contact?.useMainAccountContact) ctx.addIssue({ code: "custom", path: ["contact", "useMainAccountContact"], message: "only a dependant reads the main account's contact details (PAT-37)", params: { code: "not_allowed" } });
  if (!v.mainAccountId && v.medicalAid?.sameAsMainAccount) ctx.addIssue({ code: "custom", path: ["medicalAid", "sameAsMainAccount"], message: "only a dependant inherits the main account's medical aid (BR-17)", params: { code: "not_allowed" } });
  if (v.manualAccountCode && v.mainAccountId) ctx.addIssue({ code: "custom", path: ["manualAccountCode"], message: "a dependant's code follows its main account (BR-03)", params: { code: "not_allowed" } });
});
export type CreatePatientInput = z.infer<typeof CreatePatientInputSchema>;

/** PATCH /patients/{id} with If-Match. Family membership changes through /detach, status through /status, medical aid through its own route. */
export const PatientPatchSchema = z.strictObject({
  ...Object.fromEntries(Object.entries(PatientFieldsShape).filter(([k]) => k !== "medicalAid").map(([k, s]) => [k, (s as z.ZodType).optional()])) as { [K in Exclude<keyof typeof PatientFieldsShape, "medicalAid">]: z.ZodOptional<(typeof PatientFieldsShape)[K]> },
  /** PAT-04: the initials were typed, so a name edit must not re-derive them. */
  initialsIsManual: z.boolean().optional(),
  /** PAT-96: clear the pre-cut-over balance notice once the legacy system was checked — Owner or Admin only (a service rule, not a schema one); audited. */
  legacyBalancePossible: z.literal(false).optional(),
  /** PAT-44: dismiss (true) or restore (false) the "dependant turned 18" prompt. */
  turnedEighteenReviewed: z.boolean().optional(),
  /** D42 / BR-10: a changed ID or passport number re-runs the duplicate check (UC-PATIENTS-003 step 4). */
  duplicateOverride: DuplicateOverrideSchema.optional(),
  duplicateAcknowledged: z.boolean().optional(),
}).superRefine((v, ctx) => {
  if (Object.values(v).every((x) => x === undefined)) ctx.addIssue({ code: "custom", path: [], message: "nothing to change", params: { code: "empty" } });
  identityIssues(v, ctx, false);
  if (v.contact) {
    if (v.contact.mobile && normaliseMobile(v.contact.mobile) === null) ctx.addIssue({ code: "custom", path: ["contact", "mobile"], message: "not a phone number", params: { code: "invalid" } });
    if (v.contact.altPhone && normaliseMobile(v.contact.altPhone) === null) ctx.addIssue({ code: "custom", path: ["contact", "altPhone"], message: "not a phone number", params: { code: "invalid" } });
  }
});
export type PatientPatch = z.infer<typeof PatientPatchSchema>;

// --- Lists, search and duplicate detection (§5.6; PAT-58..PAT-61, BR-10, BR-16) -----------------------------------------------

export const PatientListQuerySchema = z.object({
  /** Repeatable: `?status=Dormant&status=Prohibited` (one value arrives as a string, several as an array). */
  status: z.union([z.enum(PATIENT_STATUSES), z.array(z.enum(PATIENT_STATUSES))]).transform((v) => (Array.isArray(v) ? v : [v])).optional(),
  branchId: UlidSchema.optional(),
  schemeId: OpaqueId.optional(),
  createdFrom: LocalDateSchema.optional(), createdTo: LocalDateSchema.optional(),
  lastVisitFrom: LocalDateSchema.optional(), lastVisitTo: LocalDateSchema.optional(),
  hasDependents: Flag.optional(),
  missingEmail: Flag.optional(),
  consentStatus: z.enum(PATIENT_CONSENT_STATUSES).optional(),
  /** Archived patients are excluded by default (BR-12). */
  includeArchived: Flag.optional(),
  legacyBalancePossible: Flag.optional(),
  /** PAT-97. */
  completeness: z.enum(["Complete", "Incomplete"]).optional(),
  createdSource: z.enum(PATIENT_CREATED_SOURCES).optional(),
  sort: z.enum(PATIENT_SORTS).optional(),
  limit: Limit, cursor: z.string().max(512).optional(), count: Flag.optional(),
}).superRefine((v, ctx) => {
  if (v.createdFrom && v.createdTo && v.createdTo < v.createdFrom) ctx.addIssue({ code: "custom", path: ["createdTo"], message: "to must not be earlier than from", params: { code: "before_from" } });
  if (v.lastVisitFrom && v.lastVisitTo && v.lastVisitTo < v.lastVisitFrom) ctx.addIssue({ code: "custom", path: ["lastVisitTo"], message: "to must not be earlier than from", params: { code: "before_from" } });
});
export type PatientListQuery = z.infer<typeof PatientListQuerySchema>;

/** PAT-58/59: typeahead after two characters; each term is an index range, never LIKE. */
export const PATIENT_SEARCH_MIN_CHARS = 2;
export const PatientSearchQuerySchema = z.object({
  q: z.string().trim().min(PATIENT_SEARCH_MIN_CHARS).max(120),
  limit: z.coerce.number().int().min(1).max(50).default(20),
  /** Keyset cursor over (surname, first names, id) issued by the previous page (§2.11); never a total count. */
  cursor: z.string().max(512).optional(),
  includeArchived: Flag.optional(),
  branchId: UlidSchema.optional(),
});
export type PatientSearchQuery = z.infer<typeof PatientSearchQuerySchema>;
export const PatientSearchResultSchema = PatientSummarySchema.extend({
  mobile: z.string().nullable(),
  cardFilingNumber: z.string().nullable(),
  matchedOn: z.array(z.enum(SEARCH_MATCH_KINDS)),
  // The six below are always sent by the Worker; optional so a reader of the foundation's row shape still parses.
  /** The Prohibited (or other) reason shown beside the badge (PAT-57). */
  statusReason: z.string().nullable().optional(),
  schemeName: z.string().nullable().optional(),
  mainAccountCode: z.string().nullable().optional(),
  /** Signed, 15 minutes; null without a current photo (PAT-30). */
  photoUrl: z.string().nullable().optional(),
  /** The status alerts only: Prohibited, Dormant, Deceased and the main-account cascade (BR-PATIENTS-008). */
  alerts: z.array(PatientAlertSchema).optional(),
  /** The local date of the last visit, null for a patient never seen (PAT-60; BR-PATIENTS-035). */
  lastVisitDate: LocalDateSchema.nullable().optional(),
});
export type PatientSearchResult = z.infer<typeof PatientSearchResultSchema>;
/** A typed phone prefix folded like the stored digits: "082 555" → "82555", "+27 82" → "82" (BR-16). */
export function phoneDigitsPrefix(term: string): string {
  const digits = term.replace(/\D/g, "");
  if (term.trim().startsWith("+27") || digits.startsWith("0027")) return digits.replace(/^0{0,2}27/, "");
  if (digits.startsWith("27") && digits.length >= 11) return digits.slice(2);
  if (digits.startsWith("0")) return digits.slice(1);
  return digits;
}
/**
 * One search term: `term` folded like the stored tokens, and — for a number — `typedDigits`, its digits as typed. Only a phone
 * sheds a leading `0` or `27` (BR-16); an ID, medical-aid or card filing number keeps it, so those are looked up by the typed
 * digits (BR-PATIENTS-009): `0803150081088`, born 2008, is never the `803150081088` its phone folding would make it.
 */
export interface SearchTerm { term: string; typedDigits: string | null }

/** The search terms of a query, folded like the stored tokens (BR-16): a phone-looking query is one digits term, an account code its lower-case form, names folded. */
export function searchQueryTerms(q: string): SearchTerm[] {
  const trimmed = q.trim();
  if (/^[+\d][\d\s()-]*$/.test(trimmed)) {
    const digits = phoneDigitsPrefix(trimmed);
    return digits.length > 0 ? [{ term: digits, typedDigits: trimmed.replace(/\D/g, "") }] : [];
  }
  return trimmed.split(/\s+/).filter(Boolean).map((term): SearchTerm => {
    if (/^[+\d][\d()-]*$/.test(term)) return { term: phoneDigitsPrefix(term), typedDigits: term.replace(/\D/g, "") };
    if (/^[A-Za-z]{3}\d{3,}(\/\d+)?$/.test(term)) return { term: term.toLowerCase(), typedDigits: null };
    if (term.includes("@")) return { term: normaliseEmail(term), typedDigits: null };
    return { term: foldName(term).replace(/\s+/g, ""), typedDigits: null };
  }).filter((t) => t.term.length > 0);
}

/** The folded terms alone — the rule the stored tokens are folded by (BR-16). */
export function searchTerms(q: string): string[] {
  return searchQueryTerms(q).map((t) => t.term);
}

export const DuplicateCheckInputSchema = z.strictObject({
  idNumber: optionalText(32),
  surname: optionalText(120),
  /** With the surname: the `possible` rule (same normalised first names and surname). */
  firstNames: optionalText(120),
  dateOfBirth: LocalDateSchema.optional(),
  mobile: optionalText(40),
  /** Leave this patient out (an edit checking itself). */
  excludePatientId: UlidSchema.optional(),
}).superRefine((v, ctx) => {
  const idRule = !!v.idNumber;
  const surnameDob = !!v.surname && !!v.dateOfBirth;
  const surnameMobile = !!v.surname && !!v.mobile;
  // The first names with the surname are enough on their own for the `possible` rule (UC-PATIENTS-003 step 1, A2): the form asks
  // as soon as both are typed, and that check was refused here, so a same-named patient was never offered (review W14).
  const surnameNames = !!v.surname && !!v.firstNames;
  if (!idRule && !surnameDob && !surnameMobile && !surnameNames) ctx.addIssue({ code: "custom", path: ["idNumber"], message: "an ID number, or a surname with a date of birth, a mobile number or the first names, is required (BR-10)", params: { code: "one_of_required" } });
});
export type DuplicateCheckInput = z.infer<typeof DuplicateCheckInputSchema>;
export const DuplicateMatchSchema = PatientSummarySchema.extend({
  rule: z.enum(DUPLICATE_MATCH_RULES),
  matchedOn: z.array(z.string()),
});
export type DuplicateMatch = z.infer<typeof DuplicateMatchSchema>;
/** BR-10 / D42: an exact ID match is `blocking` until a reason is typed (every role may); (surname, DOB) and (surname, mobile) are `probable`; (first names, surname) is `possible`. */
export const DuplicateCheckResultSchema = z.object({
  blocking: z.array(DuplicateMatchSchema),
  probable: z.array(DuplicateMatchSchema),
  possible: z.array(DuplicateMatchSchema),
  /** True when the caller may proceed past a blocking match with a reason — every role, since D42. */
  canOverride: z.boolean(),
});
export type DuplicateCheckResult = z.infer<typeof DuplicateCheckResultSchema>;

// --- Status lifecycle (§5.6 /status; BR-11, BR-12, PAT-49..PAT-54) ------------------------------------------------------------

export const StatusChangeInputSchema = z.strictObject({
  to: z.enum(PATIENT_STATUSES),
  reasonCode: z.enum(PATIENT_STATUS_REASON_CODES).optional(),
  /** Free text; the reason when no code fits, mandatory with code Other. */
  reason: optionalText(500),
}).superRefine((v, ctx) => {
  if (PATIENT_STATUSES_REQUIRING_REASON.includes(v.to) && !v.reasonCode && !v.reason) ctx.addIssue({ code: "custom", path: ["reason"], message: `a reason is required to set ${v.to}`, params: { code: "required" } });
  if (v.reasonCode === "Other" && !v.reason) ctx.addIssue({ code: "custom", path: ["reason"], message: "say what the other reason is", params: { code: "required" } });
  if (v.reasonCode === "Deceased" && v.to !== "Archived") ctx.addIssue({ code: "custom", path: ["reasonCode"], message: "Deceased is expressed through Archived (PAT-52)", params: { code: "not_allowed" } });
  if (v.reasonCode === "AutoInactive") ctx.addIssue({ code: "custom", path: ["reasonCode"], message: "AutoInactive is set by the nightly job, not by hand", params: { code: "not_allowed" } });
});
export type StatusChangeInput = z.infer<typeof StatusChangeInputSchema>;
export const PatientStatusHistoryEntrySchema = z.object({
  id: UlidSchema,
  fromStatus: z.enum(PATIENT_STATUSES).nullable(),
  toStatus: z.enum(PATIENT_STATUSES),
  reasonCode: z.enum(PATIENT_STATUS_HISTORY_REASON_CODES).nullable(),
  reason: z.string().nullable(),
  changedAt: InstantSchema,
  changedBy: UserRefSchema.nullable(),
  source: z.enum(PATIENT_STATUS_SOURCES),
  controlLogId: UlidSchema.nullable(),
});
export type PatientStatusHistoryEntry = z.infer<typeof PatientStatusHistoryEntrySchema>;

// --- Family (§5.6 /family, /detach, /account-numbers; PAT-36, PAT-39..PAT-46, BR-05a/b, Q-12) --------------------------------

export const FamilyMemberSchema = PatientSummarySchema.extend({
  dependentSuffix: z.number().int(),
  relationshipToMain: z.enum(RELATIONSHIPS_TO_MAIN).nullable(),
  /** PAT-36: claims through a scheme other than the main account's. */
  hasOwnMedicalAid: z.boolean(),
  schemeName: z.string().nullable().optional(),
  isDeceased: z.boolean(),
  /** PAT-44. */
  turnedEighteen: z.boolean(),
  turnedEighteenReviewedAt: InstantSchema.nullable().optional(),
  useMainAccountContact: z.boolean().optional(),
});
export type FamilyMember = z.infer<typeof FamilyMemberSchema>;
export const FAMILY_EVENTS = ["Attached", "DetachedNew", "DetachedToExisting", "Reparented", "Merged"] as const;
export const FAMILY_EVENT_REASONS = ["TurnedEighteen", "Divorce", "OwnMedicalAid", "Merge", "DataCorrection", "Other"] as const;
/** Who joined, who left, when and why (PAT-39..PAT-46). */
export const FamilyEventSchema = z.object({
  id: UlidSchema,
  patientId: UlidSchema,
  event: z.enum(FAMILY_EVENTS),
  oldMainAccountId: UlidSchema.nullable(),
  newMainAccountId: UlidSchema.nullable(),
  oldAccountCode: z.string().nullable(),
  newAccountCode: z.string().nullable(),
  reasonCode: z.enum(FAMILY_EVENT_REASONS).nullable(),
  reasonText: z.string().nullable(),
  openItemsDecision: z.enum([...OPEN_ITEMS_DECISIONS, "None"]).nullable(),
  openItemsCount: z.number().int().nullable(),
  openItemsValueCents: CentsSchema.nullable(),
  medicalAidChanged: z.boolean(),
  performedAt: InstantSchema,
  performedBy: UserRefSchema.nullable(),
});
export type FamilyEvent = z.infer<typeof FamilyEventSchema>;
/** The family of the patient asked for: its main account and every dependant, in suffix order, and the family's events. */
export const FamilyViewSchema = z.object({
  patientId: UlidSchema,
  mainAccount: FamilyMemberSchema,
  dependants: z.array(FamilyMemberSchema),
  /** Always sent; optional for readers of the foundation's shape. */
  events: z.array(FamilyEventSchema).optional(),
});
export type FamilyView = z.infer<typeof FamilyViewSchema>;

export const DetachInputSchema = z.strictObject({
  mode: z.enum(DETACH_MODES),
  targetMainAccountId: UlidSchema.optional(),
  /** ExistingAccount: the relationship to the new main account (PAT-34); defaults to the current one. */
  relationshipToMain: z.enum(RELATIONSHIPS_TO_MAIN).optional(),
  reason: z.enum(DETACH_REASONS),
  reasonText: optionalText(500),
  /** Q-12: open financial items stay with the original main account by default. */
  openItems: z.enum(OPEN_ITEMS_DECISIONS),
  /** PAT-39: the dependant's own membership from now on; absent = no medical aid of its own yet. */
  medicalAid: PatientMedicalAidInputSchema.nullable().optional(),
}).superRefine((v, ctx) => {
  if (v.mode === "ExistingAccount" && !v.targetMainAccountId) ctx.addIssue({ code: "custom", path: ["targetMainAccountId"], message: "the main account to attach to is required", params: { code: "required" } });
  if (v.mode === "NewAccount" && v.targetMainAccountId) ctx.addIssue({ code: "custom", path: ["targetMainAccountId"], message: "a new account has no target main account", params: { code: "not_allowed" } });
  if (v.mode === "NewAccount" && v.relationshipToMain) ctx.addIssue({ code: "custom", path: ["relationshipToMain"], message: "a new main account has no relationship to itself", params: { code: "not_allowed" } });
  if (v.reason === "Other" && !v.reasonText) ctx.addIssue({ code: "custom", path: ["reasonText"], message: "say what the other reason is", params: { code: "required" } });
  if (v.medicalAid?.sameAsMainAccount) ctx.addIssue({ code: "custom", path: ["medicalAid", "sameAsMainAccount"], message: "a detached patient no longer inherits the old main account's medical aid (BR-05a/b)", params: { code: "not_allowed" } });
});
export type DetachInput = z.infer<typeof DetachInputSchema>;
export const DetachResultSchema = z.object({
  patientId: UlidSchema,
  newAccountCode: z.string(),
  previousAccountCode: z.string(),
  newMainAccountId: UlidSchema.nullable(),
  transferredInvoiceIds: z.array(UlidSchema),
  familyEventId: UlidSchema,
  /** Q-12: the decision recorded with its snapshot; `None` when nothing was open. */
  openItems: z.object({ decision: z.enum([...OPEN_ITEMS_DECISIONS, "None"]), count: z.number().int(), valueCents: CentsSchema }).optional(),
  /** PAT-39: a same-as-main membership was closed and the patient's own row opened. */
  medicalAidChanged: z.boolean().optional(),
  /** BR-09: the former main account's contact details were copied because the patient had none of their own. */
  contactCopiedFromMainAccount: z.boolean().optional(),
});
export type DetachResult = z.infer<typeof DetachResultSchema>;

/**
 * Why a main account joins another family (UC-PATIENTS-027, BR-PATIENTS-036): it was registered as an account of its own by
 * mistake (a new patient approved from the kiosk), or another reason, in words. Values of the family event's reason CHECK.
 */
export const JOIN_FAMILY_REASONS = ["DataCorrection", "Other"] as const;
export type JoinFamilyReason = (typeof JOIN_FAMILY_REASONS)[number];
/** POST /patients/{id}/join-family: the main account to join, the relationship to it (PAT-34) and the reason (PAT-42). */
export const JoinFamilyInputSchema = z.strictObject({
  targetMainAccountId: UlidSchema,
  relationshipToMain: z.enum(RELATIONSHIPS_TO_MAIN),
  reason: z.enum(JOIN_FAMILY_REASONS),
  reasonText: optionalText(500),
}).superRefine((v, ctx) => {
  if (v.reason === "Other" && !v.reasonText) ctx.addIssue({ code: "custom", path: ["reasonText"], message: "say what the other reason is", params: { code: "required" } });
});
export type JoinFamilyInput = z.infer<typeof JoinFamilyInputSchema>;
export const JoinFamilyResultSchema = z.object({
  patientId: UlidSchema,
  /** The joined family's code with its next suffix (BR-03, PAT-46). */
  newAccountCode: z.string(),
  /** The code the patient held as a main account — kept as a searchable alias (PAT-41). */
  previousAccountCode: z.string(),
  mainAccountId: UlidSchema,
  familyEventId: UlidSchema,
});
export type JoinFamilyResult = z.infer<typeof JoinFamilyResultSchema>;

// --- Deleting a patient captured by mistake (UC-PATIENTS-028; PAT-55, PAT-56, BR-22) ------------------------------------------

/**
 * The kinds of history that keep a patient from being deleted (PAT-55, BR-PATIENTS-037), named in the refusal's
 * `meta.blockingRecordTypes`. Later modules add theirs (quotes, orders, invoices, prescriptions…) as they arrive.
 */
export const PATIENT_HISTORY_KINDS = [
  "dependant", "appointment", "bookings_inbox", "bookings_contact_link", "consent_record", "patient_document", "patient_registration_submission",
  "dsar_request", "patient_merge", "medical_history", "principal_member",
] as const;
export type PatientHistoryKind = (typeof PATIENT_HISTORY_KINDS)[number];
/** POST /patients/{id}/void: the reason, written on the record and in the Control Log (PAT-56). */
export const PatientVoidInputSchema = z.strictObject({ reason: text(500).min(1) });
export type PatientVoidInput = z.infer<typeof PatientVoidInputSchema>;
/** One line of Deleted records (BR-PATIENTS-039): a patient deleted as captured by mistake, or merged away into another. */
export const DeletedPatientEntrySchema = z.object({
  id: UlidSchema,
  accountCode: z.string(),
  displayName: z.string(),
  firstNames: z.string(),
  surname: z.string(),
  dateOfBirth: LocalDateSchema,
  homeBranchId: UlidSchema,
  kind: z.enum(["Deleted", "Merged"]),
  voidedAt: InstantSchema,
  voidedBy: UserRefSchema.nullable(),
  /** The reason given (a deletion), or "Merged into …" (a merge). */
  reason: z.string().nullable(),
  /** The Control Log row of the deletion or merge; null when the tenant took the action off its PIN-protected list. */
  controlLogId: UlidSchema.nullable(),
  /** The record a merged-away patient now lives on (UC-PATIENTS-021). */
  mergedInto: z.object({ id: UlidSchema, accountCode: z.string() }).nullable(),
});
export type DeletedPatientEntry = z.infer<typeof DeletedPatientEntrySchema>;
/** At most 50 a page: each page reads its merges in one bounded query. */
export const DeletedPatientsQuerySchema = z.object({ limit: z.coerce.number().int().min(1).max(50).default(25), cursor: z.string().max(512).optional() });

/** GET /patients/dependents-turned-18: one worklist line per dependant aged 18 or more (PAT-44). */
export const DependantTurnedEighteenEntrySchema = PatientSummarySchema.extend({
  relationshipToMain: z.enum(RELATIONSHIPS_TO_MAIN).nullable(),
  mainAccount: z.object({ id: UlidSchema, accountCode: z.string(), displayName: z.string() }),
  reviewedAt: InstantSchema.nullable(),
  reviewedBy: UserRefSchema.nullable(),
});
export type DependantTurnedEighteenEntry = z.infer<typeof DependantTurnedEighteenEntrySchema>;
export const DependantsTurnedEighteenQuerySchema = z.object({
  branchId: UlidSchema.optional(),
  includeReviewed: Flag.optional(),
  limit: Limit, cursor: z.string().max(512).optional(),
});

export const AccountNumberAliasSchema = z.object({
  accountCode: z.string(),
  accountPrefix: z.string(),
  accountSequence: z.number().int(),
  dependentSuffix: z.number().int(),
  mainAccountIdAtTime: UlidSchema.nullable(),
  validFrom: InstantSchema,
  validTo: InstantSchema.nullable(),
  changeReason: z.enum(ACCOUNT_NUMBER_CHANGE_REASONS),
  isCurrent: z.boolean(),
});
export type AccountNumberAlias = z.infer<typeof AccountNumberAliasSchema>;

// --- Coverage overrides (§5.6 /coverage-overrides; PAT-81..PAT-83; storage only in Sprint 2) ------------------------------------

const Bp0to10000 = z.number().int().min(0).max(10_000);
export const ProductCoverageOverrideInputSchema = z.strictObject({
  type: z.enum(COVERAGE_OVERRIDE_TYPES),
  rateMaxCents: NonNegativeCentsSchema.nullable().optional(),
  ratePercentBp: Bp0to10000.nullable().optional(),
  discountBp: Bp0to10000.nullable().optional(),
  effectiveFrom: LocalDateSchema.nullable().optional(),
  effectiveTo: LocalDateSchema.nullable().optional(),
  notes: optionalText(500),
}).superRefine((v, ctx) => {
  if (v.rateMaxCents == null && v.ratePercentBp == null && v.discountBp == null) ctx.addIssue({ code: "custom", path: ["rateMaxCents"], message: "at least one cap is required", params: { code: "one_of_required" } });
  if (v.effectiveFrom && v.effectiveTo && v.effectiveTo < v.effectiveFrom) ctx.addIssue({ code: "custom", path: ["effectiveTo"], message: "to must not be earlier than from", params: { code: "before_from" } });
});
export type ProductCoverageOverrideInput = z.infer<typeof ProductCoverageOverrideInputSchema>;
export const ProductCoverageOverrideViewSchema = z.object({
  id: UlidSchema, type: z.enum(COVERAGE_OVERRIDE_TYPES),
  rateMaxCents: CentsSchema.nullable(), ratePercentBp: z.number().int().nullable(), discountBp: z.number().int().nullable(),
  effectiveFrom: LocalDateSchema.nullable(), effectiveTo: LocalDateSchema.nullable(), notes: z.string().nullable(), createdAt: InstantSchema,
});
/** PUT: the whole set; rows not listed are voided (superseded, never deleted — SAL-49). */
export const ProductCoverageOverridesPutSchema = z.array(ProductCoverageOverrideInputSchema).max(50);
/** A dependant reading the main account's membership inherits its rows (BR-PATIENTS-022). */
export const CoverageInheritedFromSchema = z.object({ patientId: UlidSchema, accountCode: z.string() }).nullable();
export const ProductCoverageOverridesReadSchema = z.object({ items: z.array(ProductCoverageOverrideViewSchema), inheritedFrom: CoverageInheritedFromSchema });

export const OverallCoverageOverrideInputSchema = z.strictObject({
  membershipNumber: optionalText(40),
  overallMaxCents: NonNegativeCentsSchema.nullable().optional(),
  perMemberCents: NonNegativeCentsSchema.nullable().optional(),
  benefitYearBasis: z.enum(BENEFIT_YEAR_BASES).default("CalendarYear"),
  benefitPeriodStart: LocalDateSchema.nullable().optional(),
  benefitPeriodEnd: LocalDateSchema.nullable().optional(),
  notes: optionalText(500),
}).superRefine((v, ctx) => {
  if (v.overallMaxCents == null && v.perMemberCents == null) ctx.addIssue({ code: "custom", path: ["overallMaxCents"], message: "an overall or a per-member cap is required", params: { code: "one_of_required" } });
  if (v.benefitPeriodStart && v.benefitPeriodEnd && v.benefitPeriodEnd < v.benefitPeriodStart) ctx.addIssue({ code: "custom", path: ["benefitPeriodEnd"], message: "end must not be earlier than start", params: { code: "before_from" } });
});
export type OverallCoverageOverrideInput = z.infer<typeof OverallCoverageOverrideInputSchema>;
export const OverallCoverageOverrideViewSchema = z.object({
  id: UlidSchema, membershipNumber: z.string().nullable(),
  overallMaxCents: CentsSchema.nullable(), perMemberCents: CentsSchema.nullable(),
  benefitYearBasis: z.enum(BENEFIT_YEAR_BASES), benefitPeriodStart: LocalDateSchema.nullable(), benefitPeriodEnd: LocalDateSchema.nullable(),
  /** Derived from the ledger (PAT-82); 0 until billing exists. */
  consumedToDateCents: CentsSchema,
  notes: z.string().nullable(), createdAt: InstantSchema,
});
export const OverallCoverageOverridesPutSchema = z.array(OverallCoverageOverrideInputSchema).max(20);
export const OverallCoverageOverridesReadSchema = z.object({ items: z.array(OverallCoverageOverrideViewSchema), inheritedFrom: CoverageInheritedFromSchema });

// --- Documents (§5.6 /documents; PAT-25..PAT-30, BR-19) ---------------------------------------------------------------------

export const PatientDocumentViewSchema = z.object({
  id: UlidSchema,
  patientId: UlidSchema,
  branchId: UlidSchema,
  type: z.enum(PATIENT_DOCUMENT_TYPES),
  contentType: z.string(),
  sizeBytes: z.number().int(),
  sha256: z.string(),
  widthPx: z.number().int().nullable(),
  heightPx: z.number().int().nullable(),
  pageCount: z.number().int().nullable(),
  uploadedAt: InstantSchema,
  uploadedBy: UserRefSchema.nullable(),
  capturedAt: InstantSchema,
  capturedBy: UserRefSchema.nullable(),
  captureMethod: z.enum(CAPTURE_METHODS),
  description: z.string().nullable(),
  isCurrent: z.boolean(),
  supersededByDocumentId: UlidSchema.nullable(),
  supersededAt: InstantSchema.nullable(),
  flaggedOutdatedAt: InstantSchema.nullable(),
  linkedRecordType: z.enum(DOCUMENT_LINKED_RECORD_TYPES).nullable(),
  linkedRecordId: z.string().nullable(),
  consentRecordId: UlidSchema.nullable(),
  voidedAt: InstantSchema.nullable(),
  voidReason: z.string().nullable(),
  createdAt: InstantSchema,
});
export type PatientDocumentView = z.infer<typeof PatientDocumentViewSchema>;

/** POST /patients/{id}/documents: the file inline; a new document of a type supersedes the current one (BR-19). */
export const PatientDocumentUploadInputSchema = z.strictObject({
  type: z.enum(PATIENT_DOCUMENT_TYPES),
  file: InlineUploadSchema,
  captureMethod: z.enum(["Camera", "Upload"]).optional(),
  /** PAT-25: when the scan or photo was made; defaults to now. */
  capturedAt: InstantSchema.optional(),
  description: optionalText(500),
  linkedRecordType: z.enum(DOCUMENT_LINKED_RECORD_TYPES).optional(),
  linkedRecordId: z.string().trim().min(1).max(64).optional(),
}).superRefine((v, ctx) => {
  if (!UPLOADABLE_DOCUMENT_TYPES.includes(v.type)) ctx.addIssue({ code: "custom", path: ["type"], message: `${v.type} is not uploaded through this route`, params: { code: "not_allowed" } });
  if ((v.linkedRecordType === undefined) !== (v.linkedRecordId === undefined)) ctx.addIssue({ code: "custom", path: ["linkedRecordId"], message: "a link names both its record type and id", params: { code: "pair_required" } });
});
export type PatientDocumentUploadInput = z.infer<typeof PatientDocumentUploadInputSchema>;
/** The 201 of an upload: the new document and the id of the one it superseded (BR-19). */
export const PatientDocumentUploadedSchema = PatientDocumentViewSchema.extend({ supersededDocumentId: UlidSchema.nullable() });
export const PatientDocumentDeleteInputSchema = z.strictObject({ reason: text(500).min(1) });
export const PatientDocumentListQuerySchema = z.object({
  type: z.enum(PATIENT_DOCUMENT_TYPES).optional(),
  includeSuperseded: Flag.optional(),
  /** Voided (deleted) documents, with their reason (UC-PATIENTS-019 A1). */
  includeDeleted: Flag.optional(),
  limit: Limit, cursor: z.string().max(512).optional(), count: Flag.optional(),
});
export type PatientDocumentListQuery = z.infer<typeof PatientDocumentListQuerySchema>;

/**
 * GET /patients/{id}/document-access (UC-PATIENTS-026): who opened which of the patient's documents, newest first — the
 * document-access log (PAT-28) that every View and Download writes (BR-PATIENTS-017). Owner and Admin (`governance.access_log.read`).
 */
export const PatientDocumentAccessQuerySchema = z.object({
  /** One document's entries only — "who opened this ID scan". */
  documentId: UlidSchema.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(20), cursor: z.string().max(512).optional(),
});
export type PatientDocumentAccessQuery = z.infer<typeof PatientDocumentAccessQuerySchema>;
export const PatientDocumentAccessEntrySchema = z.object({
  id: UlidSchema,
  documentId: UlidSchema,
  /** The document's type, a deleted or superseded one's included. */
  documentType: z.enum(PATIENT_DOCUMENT_TYPES),
  action: z.enum(DOCUMENT_ACCESS_ACTIONS),
  accessedAt: InstantSchema,
  /** Null for a system send. */
  user: UserRefSchema.nullable(),
  branchId: UlidSchema.nullable(),
  /** The channel of a `Send`. */
  channel: z.string().nullable(),
  /** When the signed link the open was answered through expired (≤ 15 minutes after issue, PAT-27). */
  linkExpiresAt: InstantSchema.nullable(),
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
});
export type PatientDocumentAccessEntry = z.infer<typeof PatientDocumentAccessEntrySchema>;

// --- Merge (§5.6 /merge; PAT-65, D10b) -------------------------------------------------------------------------------------

export const MergeInputSchema = z.strictObject({
  /** The duplicate merged INTO the patient of the route; voided afterwards, never deleted. */
  mergedPatientId: UlidSchema,
  reason: text(500).min(1),
});
export type MergeInput = z.infer<typeof MergeInputSchema>;
export const MergeResultSchema = z.object({
  survivingPatientId: UlidSchema,
  mergedPatientId: UlidSchema,
  survivingAccountCode: z.string(),
  /** Kept as a historical alias. */
  mergedAccountCode: z.string(),
  movedCounts: z.record(z.string(), z.number().int()),
});
export type MergeResult = z.infer<typeof MergeResultSchema>;

// --- Kiosk registration (§5.6 /registration-submissions, §5.2 /auth/handover; PAT-68..PAT-70, BR-21, TEN-65) ---------------

/** Step 1 "Your details" (kiosk-details.html): demographics, contact, address, the ID scan and the photo. */
export const RegistrationDetailsStepSchema = z.strictObject({
  title: optionalText(20), initials: optionalText(10), firstNames: text(120).min(1), surname: text(120).min(1), knownAs: optionalText(80),
  idType: z.enum(ID_TYPES).optional(), idNumber: optionalText(32), idCountryOfIssue: Country.nullable().optional(),
  dateOfBirth: LocalDateSchema.optional(), gender: z.enum(GENDERS).optional(),
  mobile: optionalText(40), email: EmailSchema.nullable().optional(), altPhone: optionalText(40),
  preferredChannel: z.enum(CONTACT_CHANNELS).optional(), preferredLanguage: z.enum(PREFERRED_LANGUAGES).optional(),
  noElectronicContact: z.boolean().optional(),
  address: AddressInputSchema.optional(),
  postalAddress: AddressInputSchema.nullable().optional(),
  /** A dependant registering under an existing main account. */
  mainAccountId: UlidSchema.optional(),
  relationshipToMain: z.enum(RELATIONSHIPS_TO_MAIN).optional(),
  useMainAccountContact: z.boolean().optional(),
  emergencyContact: z.strictObject({ name: text(120).min(1), phone: text(40).min(1), relationship: optionalText(60) }).optional(),
  /** PAT-10: the medical history as typed on the kiosk. SPI: health. */
  medicalHistory: optionalText(20000),
  idDocument: UploadRefSchema.optional(),
  patientPhoto: UploadRefSchema.optional(),
  driversLicence: UploadRefSchema.optional(),
}).superRefine((v, ctx) => {
  identityIssues(v, ctx, false);
  if (v.mainAccountId && !v.relationshipToMain) ctx.addIssue({ code: "custom", path: ["relationshipToMain"], message: "a dependant's relationship to the main account is required (PAT-34)", params: { code: "required" } });
  if (v.mobile && normaliseMobile(v.mobile) === null) ctx.addIssue({ code: "custom", path: ["mobile"], message: "not a phone number", params: { code: "invalid" } });
});
export type RegistrationDetailsStep = z.infer<typeof RegistrationDetailsStepSchema>;

/** Step 2 "Medical aid" (kiosk-medical-aid.html): the membership, the principal member and the card scans. */
export const RegistrationMedicalAidStepSchema = z.strictObject({
  hasMedicalAid: z.boolean(),
  schemeId: OpaqueId.nullable().optional(),
  /** Typed when the scheme is not in the list; resolved by staff at approval. */
  schemeName: optionalText(120),
  planId: OpaqueId.nullable().optional(), planName: optionalText(120),
  optionId: OpaqueId.nullable().optional(), optionName: optionalText(120),
  membershipNumber: optionalText(40),
  dependentCode: optionalText(10),
  /** "I am on a different scheme from the account holder" unticked = same as the main account. */
  sameAsMainAccount: z.boolean().optional(),
  principalMemberIs: z.enum(PRINCIPAL_MEMBER_IS).optional(),
  principalMember: PrincipalMemberBlockInputSchema.nullable().optional(),
  cardFront: UploadRefSchema.optional(),
  cardBack: UploadRefSchema.optional(),
}).superRefine((v, ctx) => {
  if (v.hasMedicalAid && !v.sameAsMainAccount && !v.schemeId && !v.schemeName && !v.membershipNumber) ctx.addIssue({ code: "custom", path: ["schemeId"], message: "the scheme or the membership number is required", params: { code: "required" } });
  if (v.principalMemberIs === "OtherPerson" && !v.principalMember) ctx.addIssue({ code: "custom", path: ["principalMember"], message: "the principal member's details are required", params: { code: "required" } });
});
export type RegistrationMedicalAidStep = z.infer<typeof RegistrationMedicalAidStepSchema>;

/** Step 3 "Consent" (kiosk.html): the purposes, the declaration and the signature; the channel is InStoreTablet, set by the server. */
export const RegistrationConsentStepSchema = z.strictObject({ ...ConsentInputShape }).superRefine((v, ctx) => consentInputIssues({ ...v, channel: "InStoreTablet" }, ctx));
export type RegistrationConsentStep = z.infer<typeof RegistrationConsentStepSchema>;

/** POST /registration-submissions — the one write a handover token reaches (§5.2). */
export const RegistrationSubmissionInputSchema = z.strictObject({
  kind: z.enum(REGISTRATION_KINDS),
  existingPatientId: UlidSchema.optional(),
  /** PAT-78 hook: pre-registration reached from a booking. */
  appointmentId: UlidSchema.optional(),
  /** Which form produced the payload, so an old submission still renders. */
  formVersion: z.string().trim().min(1).max(20),
  details: RegistrationDetailsStepSchema,
  medicalAid: RegistrationMedicalAidStepSchema,
  consent: RegistrationConsentStepSchema,
}).superRefine((v, ctx) => {
  if (v.kind === "UpdateExisting" && !v.existingPatientId) ctx.addIssue({ code: "custom", path: ["existingPatientId"], message: "an update names the patient", params: { code: "required" } });
  if (v.kind === "NewPatient" && v.existingPatientId) ctx.addIssue({ code: "custom", path: ["existingPatientId"], message: "a new patient has no existing record", params: { code: "not_allowed" } });
});
export type RegistrationSubmissionInput = z.infer<typeof RegistrationSubmissionInputSchema>;
export const REGISTRATION_FORM_VERSION = "kiosk-2026-09";
/** `201` of POST /registration-submissions: the id and `Pending`, nothing else, so the tablet never sees a patient's details (UC-PATIENTS-023 step 6). */
export const RegistrationSubmissionReceiptSchema = z.object({ id: UlidSchema, status: z.literal("Pending") });
export type RegistrationSubmissionReceipt = z.infer<typeof RegistrationSubmissionReceiptSchema>;

/**
 * The consent step as staff read it back — never the signature bytes. The guardian block is the consent contract's
 * (GuardianBlockSchema: relationship, ID number — `identity`, shown to the reviewing staff as every identity field is — and
 * the guardian's patient id when they are a patient); the guardian's name is the signer's name (null = the patient's own).
 */
export const RegistrationConsentViewSchema = z.object({
  purposes: z.array(z.object({ purpose: z.string(), channel: z.string().nullable(), granted: z.boolean() })),
  textVersion: z.number().int(),
  signerName: z.string().nullable(),
  signerType: z.string(),
  guardian: z.object({ relationship: z.enum(GUARDIAN_RELATIONSHIPS), idNumber: z.string(), patientId: UlidSchema.nullable() }).nullable(),
  acknowledgedItems: z.array(z.string()),
  hasSignature: z.boolean(),
});
export const RegistrationPayloadViewSchema = z.object({
  details: z.record(z.string(), z.unknown()),
  medicalAid: z.record(z.string(), z.unknown()),
  consent: RegistrationConsentViewSchema,
  /** The kiosk captures stored as documents already (ID, photo, card front/back), by type. */
  captures: z.array(z.object({ type: z.enum(PATIENT_DOCUMENT_TYPES), documentId: UlidSchema })),
});
export const RegistrationSubmissionViewSchema = z.object({
  id: UlidSchema,
  branchId: UlidSchema,
  source: z.enum(REGISTRATION_SOURCES),
  kind: z.enum(REGISTRATION_KINDS),
  existingPatientId: UlidSchema.nullable(),
  appointmentId: UlidSchema.nullable(),
  status: z.enum(REGISTRATION_STATUSES),
  submittedAt: InstantSchema,
  reviewedBy: UserRefSchema.nullable(),
  reviewedAt: InstantSchema.nullable(),
  rejectionReason: z.string().nullable(),
  resultingPatientId: UlidSchema.nullable(),
  consentRecordId: UlidSchema.nullable(),
  /** BR-10 candidates shown to the reviewer (PAT-64). */
  duplicateMatches: z.array(DuplicateMatchSchema),
  formVersion: z.string(),
  payload: RegistrationPayloadViewSchema,
  openedBy: UserRefSchema.nullable(),
  /** PAT-70: a rejected submission is purged 30 days after its rejection (always sent). */
  purgeAfter: InstantSchema.nullable().optional(),
  createdAt: InstantSchema,
});
export type RegistrationSubmissionView = z.infer<typeof RegistrationSubmissionViewSchema>;
/** The images a kiosk submission can carry, in the order the steps capture them (UC-PATIENTS-023 steps 1–3). */
export const REGISTRATION_CAPTURE_SLOTS = ["idDocument", "patientPhoto", "driversLicence", "cardFront", "cardBack", "signature"] as const;
export type RegistrationCaptureSlot = (typeof REGISTRATION_CAPTURE_SLOTS)[number];
/** One captured image as the reviewer opens it: a signed link to the object under the submission's own prefix (BR-PATIENTS-033). */
export const RegistrationCaptureLinkSchema = z.object({
  slot: z.enum(REGISTRATION_CAPTURE_SLOTS),
  /** The document type the image becomes at approval; null for the consent signature, which goes with the consent record. */
  documentType: z.enum(PATIENT_DOCUMENT_TYPES).nullable(),
  contentType: z.string(),
  sizeBytes: z.number().int(),
  widthPx: z.number().int().nullable(),
  heightPx: z.number().int().nullable(),
  /** `GET /files/{token}`: valid at most 15 minutes, for the user it was issued to only (TEN-70). */
  url: z.string(),
  expiresAt: InstantSchema,
});
export type RegistrationCaptureLink = z.infer<typeof RegistrationCaptureLinkSchema>;
/**
 * What a kiosk submission can change on an existing record, as the review names a change made on the record since the kiosk
 * read it (BR-PATIENTS-032): the details by the submission's own paths, and the membership as one.
 */
export const REGISTRATION_RECORD_FIELDS = [
  "details.title", "details.initials", "details.firstNames", "details.surname", "details.knownAs", "details.idNumber", "details.dateOfBirth", "details.gender",
  "details.mobile", "details.email", "details.altPhone", "details.preferredChannel", "details.preferredLanguage", "details.noElectronicContact", "details.useMainAccountContact",
  "details.address", "details.postalAddress", "details.medicalHistory", "medicalAid",
] as const;
export type RegistrationRecordField = (typeof REGISTRATION_RECORD_FIELDS)[number];
/** One change made on the record since the kiosk read it that the approval would overwrite, for the reviewer to decide. */
export const RegistrationRecordChangeSchema = z.object({
  field: z.enum(REGISTRATION_RECORD_FIELDS),
  /** As the record stood when the kiosk read it; null when empty, or when it was not kept (`atSubmissionKept: false`). */
  atSubmission: z.string().nullable(),
  /** False for a field staff changed while the patient was at the kiosk, or a submission made before the facts were kept. */
  atSubmissionKept: z.boolean(),
  /**
   * True when the record shows this field was changed after the kiosk read it; false when only that the record (or its
   * membership) moved on since is known — a submission whose handover kept no digests (BR-PATIENTS-032): who changed what cannot
   * be told, and it may be the patient's own change.
   */
  changeKnown: z.boolean(),
  /** On the record now. */
  onRecord: z.string().nullable(),
  /** What the approval would write: the patient's value, with the reviewer's corrections. */
  submitted: z.string().nullable(),
  /** The values are of a class the reviewer's role may not see (medical history for a Receptionist): all three are null. */
  redacted: z.boolean(),
});
export type RegistrationRecordChange = z.infer<typeof RegistrationRecordChangeSchema>;
export const RegistrationRecordChangesSchema = z.object({
  /** The record's version now: the approval's `recordChanges.patientVersion` names it. */
  patientVersion: z.number().int(),
  /** The version the kiosk read; null for a submission made before it was kept. */
  versionAtSubmission: z.number().int().nullable(),
  changes: z.array(RegistrationRecordChangeSchema),
});
export type RegistrationRecordChanges = z.infer<typeof RegistrationRecordChangesSchema>;
/** GET /registration-submissions/{id}: the queue row and what its review shows beyond it (UC-PATIENTS-024 step 2, A9). */
export const RegistrationSubmissionDetailSchema = RegistrationSubmissionViewSchema.extend({
  /** While Pending, a link to each image the kiosk captured; empty once reviewed — an approved submission's are the patient's documents. */
  captureLinks: z.array(RegistrationCaptureLinkSchema),
  /** While Pending, the changes made on the record since the kiosk read it that the approval would overwrite; null when none. */
  recordChanges: RegistrationRecordChangesSchema.nullable(),
});
export type RegistrationSubmissionDetail = z.infer<typeof RegistrationSubmissionDetailSchema>;
/** POST /registration-submissions/{id}/approve — creates or updates the official record after the duplicate check (PAT-70). */
export const RegistrationApproveInputSchema = z.strictObject({
  /** D42: create anyway past an exact-ID match, naming the matched patient and the reason. */
  duplicateOverride: DuplicateOverrideSchema.optional(),
  /** BR-10: create past a probable match once it has been seen. */
  duplicateAcknowledged: z.boolean().optional(),
  /** Apply the submission to this matched patient as an update instead of creating a record (UC-PATIENTS-024 A2). */
  useExistingPatientId: UlidSchema.optional(),
  /** Attach as a dependant of this main account (overrides the submission's choice). */
  mainAccountId: UlidSchema.optional(),
  homeBranchId: UlidSchema.optional(),
  cardFilingNumber: optionalText(20),
  /** Staff-resolved scheme/plan/option when the patient typed names. */
  medicalAid: PatientMedicalAidInputSchema.nullable().optional(),
  /** Staff corrections merged over the payload before the full validation (UC-PATIENTS-024 A3); audited as the approver's. */
  corrections: z.strictObject({
    details: z.record(z.string(), z.unknown()).optional(),
    medicalAid: z.record(z.string(), z.unknown()).optional(),
  }).optional(),
  /**
   * BR-PATIENTS-032: the reviewer's decision on each change made on the record since the kiosk read it — the record's value
   * kept, or the patient's taken — against the record's version the review showed.
   */
  recordChanges: z.strictObject({
    patientVersion: z.number().int().min(1),
    keep: z.array(z.enum(REGISTRATION_RECORD_FIELDS)).max(REGISTRATION_RECORD_FIELDS.length),
    take: z.array(z.enum(REGISTRATION_RECORD_FIELDS)).max(REGISTRATION_RECORD_FIELDS.length),
  }).optional(),
});
export type RegistrationApproveInput = z.infer<typeof RegistrationApproveInputSchema>;
export const RegistrationApproveResultSchema = z.object({
  patientId: UlidSchema,
  accountCode: z.string(),
  created: z.boolean(),
  consentRecordId: UlidSchema.nullable(),
  documentIds: z.array(UlidSchema),
});
export type RegistrationApproveResult = z.infer<typeof RegistrationApproveResultSchema>;
export const RegistrationRejectInputSchema = z.strictObject({ reason: text(500).min(1) });
export const RegistrationListQuerySchema = z.object({
  status: z.enum(REGISTRATION_STATUSES).optional(),
  branchId: UlidSchema.optional(),
  /** A patient record's own submissions (UC-PATIENTS-024 A5). */
  existingPatientId: UlidSchema.optional(),
  limit: Limit, cursor: z.string().max(512).optional(), count: Flag.optional(),
});
export type RegistrationListQuery = z.infer<typeof RegistrationListQuerySchema>;

/** POST /auth/handover (§5.2, TEN-65, BR-21): enter kiosk mode. */
export const HandoverRequestSchema = z.strictObject({
  patientId: UlidSchema.optional(),
  formType: z.enum(HANDOVER_FORM_TYPES),
}).superRefine((v, ctx) => {
  if (v.formType !== "Registration" && !v.patientId) ctx.addIssue({ code: "custom", path: ["patientId"], message: `${v.formType} needs the patient whose record it updates`, params: { code: "required" } });
  if (v.formType === "Registration" && v.patientId) ctx.addIssue({ code: "custom", path: ["patientId"], message: "a new registration has no patient yet; use Update for an existing one", params: { code: "not_allowed" } });
});
export type HandoverRequest = z.infer<typeof HandoverRequestSchema>;
/** The only routes a handover token reaches (§5.2): one submission plus the lists the form needs. */
export const HANDOVER_ROUTE_ALLOWLIST: readonly string[] = [
  "POST /registration-submissions",
  // The handed-over patient only (an existing patient's re-consent, D62).
  "GET /patients/{id}/consents/form",
  "POST /patients/{id}/consents",
  // The titles come with the handover answer's form; the kiosk reads no other settings.
  "GET /reference/medical-aid/schemes",
  "GET /reference/medical-aid/plans",
  "GET /reference/medical-aid/options",
  "GET /reference/medical-aid/options/{id}",
];
/** TEN-65 / BR-21: the kiosk clears after this long idle unless the organisation sets otherwise (organisation.handoverIdleMinutes). */
export const HANDOVER_IDLE_MINUTES_DEFAULT = 10;
/** What the kiosk pre-fills for an existing patient (UC-PATIENTS-022 step 3): the patient's own details, never another patient's. */
export const HandoverPrefillSchema = z.object({
  title: z.string().nullable(), initials: z.string().nullable(), firstNames: z.string(), surname: z.string(), knownAs: z.string().nullable(),
  idType: z.enum(ID_TYPES), idNumber: z.string().nullable(), idCountryOfIssue: z.string().nullable(),
  dateOfBirth: LocalDateSchema, gender: z.enum(GENDERS),
  mobile: z.string().nullable(), email: z.string().nullable(), altPhone: z.string().nullable(),
  preferredChannel: z.enum(CONTACT_CHANNELS), preferredLanguage: z.enum(PREFERRED_LANGUAGES),
  address: AddressSchema, postalAddress: AddressSchema.nullable(),
  /** Staff-entered; shown read-only on the kiosk. */
  cardFilingNumber: z.string().nullable(),
  mainAccountId: UlidSchema.nullable(),
  relationshipToMain: z.enum(RELATIONSHIPS_TO_MAIN).nullable(),
  useMainAccountContact: z.boolean(),
  medicalAid: PatientMedicalAidViewSchema.nullable(),
});
export type HandoverPrefill = z.infer<typeof HandoverPrefillSchema>;
/**
 * What the three kiosk steps need beyond the patient: the titles, the consent text and the purposes with their D43 defaults
 * and the Owner's wording (UC-GOVERNANCE-016), and the privacy contact the notice names. A new patient has no record, so no
 * consent form can be read under the token: this is the practice's wording the kiosk shows them (UC-PATIENTS-022 step 3).
 */
export const HandoverFormSchema = z.object({
  titles: z.array(z.object({ code: z.string(), label: z.string() })),
  consentText: z.object({ version: z.number().int(), bodyText: z.string(), effectiveFrom: LocalDateSchema }).nullable(),
  purposes: z.array(z.object({ purpose: z.string(), label: z.string(), description: z.string(), required: z.boolean(), preTicked: z.boolean(), channelScoped: z.boolean() })),
  /** The consent settings' privacy contact, else the Information Officer's email; null when neither is set. */
  privacyContactEmail: z.string().nullable(),
});
export type HandoverForm = z.infer<typeof HandoverFormSchema>;
export const HandoverResponseSchema = z.object({
  handoverToken: z.string(),
  expiresAt: InstantSchema,
  formType: z.enum(HANDOVER_FORM_TYPES),
  /** The handed-over patient; null for a new registration. */
  patientId: UlidSchema.nullable(),
  branchId: UlidSchema,
  /** The tenant's handover idle minutes (TEN-65; organisation.handoverIdleMinutes, default 10). */
  idleMinutes: z.number().int().default(HANDOVER_IDLE_MINUTES_DEFAULT),
  allowedRoutes: z.array(z.string()),
  /** The consent text version the kiosk presents, and the D43 defaults. */
  consentTextVersion: z.number().int().nullable(),
  /** Always sent (null for a new registration); optional for readers of the foundation's shape. */
  prefill: HandoverPrefillSchema.nullable().optional(),
  form: HandoverFormSchema.optional(),
});
export type HandoverResponse = z.infer<typeof HandoverResponseSchema>;
export const HandoverExitResponseSchema = z.object({ exitedAt: InstantSchema, submissionId: UlidSchema.nullable() });
/**
 * POST /auth/handover/activity (BR-PATIENTS-028): the patient is still at the tablet. The kiosk sends it at most once a minute
 * while the screen is being touched, so a patient filling in a step that makes no other request keeps the handover open.
 */
export const HandoverActivityResponseSchema = z.object({ expiresAt: InstantSchema });
export type HandoverActivityResponse = z.infer<typeof HandoverActivityResponseSchema>;
/** The session view's `handover` block while a session is in kiosk mode (UC-PATIENTS-022 step 2). */
export const SessionHandoverSchema = z.object({ formType: z.enum(HANDOVER_FORM_TYPES), patientId: UlidSchema.nullable(), enteredAt: InstantSchema, expiresAt: InstantSchema });
export type SessionHandover = z.infer<typeof SessionHandoverSchema>;

// --- Titles (§5.6 /patient-titles; PAT-02, Q-01) ----------------------------------------------------------------------------

export const PatientTitleSchema = z.object({
  id: UlidSchema,
  code: z.string(),
  label: z.string(),
  sortOrder: z.number().int(),
  isActive: z.boolean(),
  isSeeded: z.boolean(),
});
export type PatientTitle = z.infer<typeof PatientTitleSchema>;
export const PatientTitleInputSchema = z.strictObject({
  code: z.string().trim().min(1).max(20).regex(/^[A-Za-z][A-Za-z.]*$/, "letters only, e.g. Ms or Adv"),
  label: text(40).min(1),
  sortOrder: z.number().int().min(0).max(1000).optional(),
});
export type PatientTitleInput = z.infer<typeof PatientTitleInputSchema>;
/** DELETE /patient-titles names the code; the row is deactivated, never deleted (PAT-02). */
export const PatientTitleRemoveSchema = z.strictObject({ code: z.string().trim().min(1).max(20) });

export type { PatientStatus };
